import { useDroppable } from '@dnd-kit/react';
import { isNotDefined } from '@sgme/fp';
import clsx from 'clsx';
import { useCallback, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { shallowEqual } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { useCurrentUserQuery } from '@/api/user/index.ts';
import { selectGetWorkspace } from '@/api/workspace/index.ts';
import { CURSOR_WHEN_DRAGGING } from '@/App/Grid/DragBar.tsx';
import { DraggableTile } from '@/App/Grid/DraggableTile.tsx';
import { DragManager, type DragManagerProps } from '@/App/Grid/DragManager';
import { TileHeightObserverProvider } from '@/App/Grid/tileHeightObserverContext.tsx';
import { TileSkeleton } from '@/App/Grid/TileSkeleton.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectActiveTabId } from '@/store/tabs/slice.ts';
import { selectTilesOfTab } from '@/store/tiles/selectors.ts';
import { addTile, moveTile } from '@/store/tiles/slice.ts';
import type { Tile } from '@/store/tiles/types.ts';
import type { GridCoordinates, PxCoordinates } from '@/types/grid.ts';
import { gridToPx, pxToGrid } from '@/utils/grid/coordinates.ts';
import { updateElementAbsolutePosition } from '@/utils/grid/updateElementAbsolutePosition.ts';
import { ZINDEX_TILE, ZINDEX_TILE__DRAGGED, ZINDEX_TILESKELETON } from '@/utils/grid/zIndices.ts';
import { useThrottle } from '@/utils/hooks/useThrottle.ts';

import type { DragState } from './DragState';
import { dragStateNoDrag } from './DragState';

const UPDATE_POSITION_THROTTLE_DELAY = 17; // in ms, approx equiv. 60Hz

export type GridProps = {
  className?: string;
};

export function Grid({ className }: GridProps) {
  const dispatch = useAppDispatch();
  const currentTabId = useAppSelector(selectActiveTabId);
  const tiles = useAppSelector((state) => selectTilesOfTab(state, currentTabId), shallowEqual);

  const [dragState, setDragState] = useState<DragState>(dragStateNoDrag);

  const { isLoading: isWorkspaceLoading } = useAppSelector(selectGetWorkspace());
  const userPreferences = useAppSelector((state) => state.userPreferences);
  const { data: currentUser } = useCurrentUserQuery();
  const defaultProduct = currentUser?.productTypes[0] ?? 'Cash';

  const { ref: refOnDroppable } = useDroppable({
    id: 'grid',
    accept: 'tile',
  });

  const skeletonRef = useRef<HTMLDivElement | null>(null);

  const hideSkeleton = useCallback(() => {
    if (isNotDefined(skeletonRef.current)) {
      throw Error('hideSkeleton call with no ref to skeleton');
    }
    skeletonRef.current.style.display = 'none';
  }, []);

  const resetState = useCallback(() => {
    animationFrameRequestId.current = 0;
    hideSkeleton();
    setDragState(dragStateNoDrag);
  }, [hideSkeleton]);

  const getSkeletonPxPosition = useCallback<() => PxCoordinates>(() => {
    if (isNotDefined(skeletonRef.current)) {
      throw Error('getSkeletonPxPosition call with no ref to skeleton');
    }
    return {
      x: Number.parseInt(skeletonRef.current.style.left, 10),
      y: Number.parseInt(skeletonRef.current.style.top, 10),
    };
  }, []);

  // we need two different instances of throttling for the same function here
  // or the same throttling will apply to both separate modifications : the dragged tile and the skeletton
  // it would prevent on of the two to be executed at the required pace
  const throttledPositionUpdateDuringDragForDraggedTile = useThrottle(
    updateElementAbsolutePosition,
    UPDATE_POSITION_THROTTLE_DELAY,
  );
  const throttledPositionUpdateDuringDragForSkeleton = useThrottle(
    updateElementAbsolutePosition,
    UPDATE_POSITION_THROTTLE_DELAY,
  );

  // this is required when drag ends
  // we have to define the definitve position the dragged tile after drag
  // but an animation frame may have been requested on the previous call to handleDragMove !
  const animationFrameRequestId = useRef<number>(0);

  const onDragStart = useCallback<DragManagerProps['onDragStart']>(
    ({ tile }) => {
      // skeleton has to appear now, this would prevent the tile to be displaced in a strange place
      // in case of 'no-move' drag (long click and release)
      throttledPositionUpdateDuringDragForSkeleton({
        element: skeletonRef.current,
        position: gridToPx({
          col: tile.x,
          row: tile.y,
        }),
        zIndex: ZINDEX_TILESKELETON,
      });
      setDragState({ isDragInProgress: true, draggedTile: tile, tiles });
    },
    [tiles],
  );

  const onDragMove = useCallback<DragManagerProps['onDragMove']>(
    ({ tileElement, draggedTileCurrentPosition, skeletonPosition }) => {
      animationFrameRequestId.current = requestAnimationFrame(() => {
        throttledPositionUpdateDuringDragForDraggedTile({
          element: tileElement,
          position: draggedTileCurrentPosition,
          zIndex: ZINDEX_TILE__DRAGGED,
        });
        animationFrameRequestId.current = requestAnimationFrame(() => {
          throttledPositionUpdateDuringDragForSkeleton({
            element: skeletonRef.current,
            position: skeletonPosition,
            zIndex: ZINDEX_TILESKELETON,
          });
        });
      });
    },
    [],
  );

  const onLayoutChanged = useCallback<DragManagerProps['onLayoutChanged']>(
    ({ tiles }) => {
      if (!dragState.isDragInProgress) {
        console.error('state inconstancy in onLayoutChanged');
        return;
      }
      setDragState({ isDragInProgress: true, tiles: tiles, draggedTile: dragState.draggedTile });
    },
    [dragState],
  );

  const onDragCanceled = useCallback<DragManagerProps['onDragCanceled']>(
    ({ draggedTileElement, position }) => {
      // restore tile position to before drag started
      updateElementAbsolutePosition({
        element: draggedTileElement,
        position,
        zIndex: ZINDEX_TILE,
      });

      resetState();
    },
    [resetState],
  );

  const onDragEnd = useCallback<DragManagerProps['onDragEnd']>(
    ({ draggedTile, draggedTileElement, position }) => {
      if (isNotDefined(skeletonRef.current)) {
        return;
      }

      if (animationFrameRequestId.current) {
        cancelAnimationFrame(animationFrameRequestId.current);
      }

      animationFrameRequestId.current = requestAnimationFrame(() => {
        throttledPositionUpdateDuringDragForDraggedTile({
          element: draggedTileElement,
          position,
          zIndex: ZINDEX_TILE,
        });
      });

      // don't give the computed position after compaction here, cause reducer will compute compaction again,
      // with the same data the grid used during drag, so with the same effect
      const skeletonPosition = getSkeletonPxPosition();
      const newTileGridCoordinates = pxToGrid(skeletonPosition);

      if (newTileGridCoordinates.col !== draggedTile.x || newTileGridCoordinates.row !== draggedTile.y) {
        dispatch(
          moveTile({
            tileId: draggedTile.tileId,
            x: newTileGridCoordinates.col,
            y: newTileGridCoordinates.row,
          }),
        );
      }

      resetState();
    },
    [getSkeletonPxPosition, resetState],
  );

  const handleHover = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (dragState.isDragInProgress) {
        // preventing skeleton flickering when we are currently dragging some tile
        return;
      }
      const cellUnderMouse = getCellUnderMouse(e);

      const cellForCreatingTile = getLowestCellForCreatingTile(cellUnderMouse, tiles);

      if (isNotDefined(cellForCreatingTile)) {
        hideSkeleton();
        return;
      }

      animationFrameRequestId.current = requestAnimationFrame(() => {
        throttledPositionUpdateDuringDragForSkeleton({
          element: skeletonRef.current,
          position: gridToPx(cellForCreatingTile),
          zIndex: ZINDEX_TILESKELETON,
        });
      });
    },
    [hideSkeleton, dragState.isDragInProgress, tiles],
  );

  const handleClickOnSkeleton = useCallback(() => {
    if (isNotDefined(skeletonRef.current)) {
      console.error('handleClickOnSkeleton without skeleton');
      return;
    }

    const skeletonGridPosition = pxToGrid(getSkeletonPxPosition());

    hideSkeleton();

    dispatch(
      addTile({
        tile: {
          tileId: uuid(),
          tabId: currentTabId,
          productType: defaultProduct,
          x: skeletonGridPosition.col,
          y: skeletonGridPosition.row,
          h: 1,
          w: 1,
        },
        userPreferences,
        sessionId: uuid(),
      }),
    );
  }, [userPreferences, defaultProduct, currentTabId, hideSkeleton, getSkeletonPxPosition]);

  // avoid showing the grid while workspace is loading, because workspace response override everything
  if (isWorkspaceLoading) {
    return <Spinner className="mx-auto my-auto" />;
  }

  return (
    <TileHeightObserverProvider>
      <div className={clsx('position-relative d-flex h-100', className)}>
        <DragManager
          tiles={tiles}
          onDragStart={onDragStart}
          onDragMove={onDragMove}
          onLayoutChanged={onLayoutChanged}
          onDragEnd={onDragEnd}
          onDragCanceled={onDragCanceled}
        >
          <div
            ref={refOnDroppable}
            data-e2e="grid"
            style={{
              position: 'relative',
              width: '100%',
              overflow: 'auto',
              // this avoid a glitch when dragging a tile (cursor is intermittently outside the dragbar, flickering the cursor pointer)
              cursor: dragState.isDragInProgress ? CURSOR_WHEN_DRAGGING : 'auto',
            }}
            onMouseMove={handleHover}
          >
            {(dragState.isDragInProgress ? dragState.tiles : tiles).map((tile) => (
              <DraggableTile key={tile.tileId} {...tile} />
            ))}

            <TileSkeleton
              ref={skeletonRef}
              span={
                dragState.isDragInProgress ? { col: dragState.draggedTile.w, row: dragState.draggedTile.h } : undefined
              }
              onClick={dragState.isDragInProgress ? undefined : handleClickOnSkeleton}
            />
          </div>
        </DragManager>
      </div>
    </TileHeightObserverProvider>
  );
}

function getCellUnderMouse(e: React.MouseEvent): GridCoordinates {
  const rect = e.currentTarget.getBoundingClientRect();
  const scrollLeft = e.currentTarget.scrollLeft;
  const scrollTop = e.currentTarget.scrollTop;

  return pxToGrid({
    x: e.clientX - rect.left + scrollLeft,
    y: e.clientY - rect.top + scrollTop,
  });
}

/**
 * This function tells where we should create a tile knowing where the hovering mouse is on the grid.
 * - one can't create a tile where another tile lays
 * - if two tiles are below (is the sense of the y/row axis, read "above" in term of visual layout)
 *   then the higher (regarding y/row) is influent
 * - if no tile is influencing then we can create a tile at the bottom (row=0)
 * @param limitCell where the mouse is on the grid
 * @param tiles all tiles present in the grid
 * @returns the position where to create a tile or undefined if not allowed
 */
// find the lowest cell (in Y) available in the grid for tile creation, up to limitCell
function getLowestCellForCreatingTile(
  { col: limitCellCol, row: limitCellRow }: GridCoordinates,
  tiles: Tile[],
): GridCoordinates | undefined {
  let rowForNewTile = -1;

  for (const tile of tiles) {
    if (limitCellCol < tile.x || limitCellCol >= tile.x + tile.w) {
      // the tile does not span over limitCell.x: the tile is not influencing
      continue;
    }

    if (limitCellRow < tile.y) {
      // too high: the tile is not influencing
      continue;
    }

    if (limitCellRow <= tile.y + tile.h) {
      // tile covers the limit cell (cause we know know that limitCell.row >= tile.y)
      // we can't create a tile here
      return undefined;
    }

    rowForNewTile = Math.max(rowForNewTile, tile.y + tile.h);
  }

  return {
    col: limitCellCol,
    row: Math.max(0, rowForNewTile), // if -1 (no influencing tile found) then we can create it at row=0
  };
}
